import { Button, Grid, Tooltip } from '@material-ui/core';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { catalogEntityReadPermission } from '@backstage/plugin-catalog-common/alpha';
import { EmptyState } from '@backstage/core-components';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { createFrontendModule, Progress } from '@backstage/frontend-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  EntityContentBlueprint,
  EntityContextMenuItemBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  EntityAccessBindingsContent,
  accessBindingListPermission,
} from 'adp-access-react';
import { EntityArgoCDOverviewCard, isArgocdAvailable } from 'plugin-argo-cd';
import {
  GitflicIssuesMergeRequestsContent,
  GitflicPipelinesCard,
  isGitflicAvailable,
} from 'plugin-gitflic-pipelines';
import { useNavigate } from 'react-router-dom';
import { appTranslationRef } from '../../translation';
import { Alert } from '@backstage/ui';

const cicdComponentTypes = new Set(['service', 'infrastructure', 'website']);
const bindingResourceTypes = new Set(['kubernetes-cluster', 'project']);

const isComponent = (entity: Entity) =>
  entity.kind.toLocaleLowerCase('en-US') === 'component';

const isCicdComponent = (entity: Entity) =>
  isComponent(entity) &&
  cicdComponentTypes.has(String(entity.spec?.type).toLocaleLowerCase('en-US'));

export const buildTemplateUrl = (templateName: string, entity: Entity) => {
  const formData = new URLSearchParams({
    formData: JSON.stringify({
      componentRef: stringifyEntityRef(entity),
    }),
  });

  return `/create/templates/default/${templateName}?${formData.toString()}`;
};

type TemplateAvailability = {
  loading: boolean;
  allowed: boolean;
};

export function useTemplateAvailability(
  templateRef: string,
): TemplateAvailability {
  const { loading, allowed } = usePermission({
    permission: catalogEntityReadPermission,
    resourceRef: templateRef,
  });

  return { loading, allowed };
}

const isBindingsAvailable = (entity: Entity) => {
  const kind = entity.kind.toLocaleLowerCase('en-US');

  if (kind === 'component' || kind === 'user' || kind === 'group') {
    return true;
  }

  return (
    kind === 'resource' &&
    bindingResourceTypes.has(
      String(entity.spec?.type).toLocaleLowerCase('en-US'),
    )
  );
};

// TODO: стоит добавить скрытие таба но пока нельзя в backstage
// см. https://github.com/backstage/backstage/issues/31220
function BindingsContent() {
  const { t } = useTranslationRef(appTranslationRef);
  const { allowed, loading } = usePermission({
    permission: accessBindingListPermission,
  });

  if (loading) {
    return <Progress />;
  }

  if (!allowed) {
    return (
      <Alert
        status="warning"
        icon
        title={t('entityPage.bindingsAccessWarning.title')}
        description={t('entityPage.bindingsAccessWarning.description')}
      />
    );
  }

  return <EntityAccessBindingsContent />;
}

const CicdEmptyState = () => {
  const { t } = useTranslationRef(appTranslationRef);

  return (
    <EmptyState
      title={t('entityPage.cicdEmptyState.title')}
      missing="info"
      description={t('entityPage.cicdEmptyState.description')}
      action={
        <Button
          variant="contained"
          color="primary"
          href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
        >
          {t('entityPage.cicdEmptyState.readMore')}
        </Button>
      }
    />
  );
};

const CicdContent = () => {
  const { entity } = useEntity();
  const gitflicAvailable = isGitflicAvailable(entity);
  const argocdAvailable = Boolean(isArgocdAvailable(entity));

  return (
    <Grid container spacing={3} alignItems="stretch">
      {gitflicAvailable && (
        <Grid item xs={12}>
          <GitflicPipelinesCard />
        </Grid>
      )}
      {argocdAvailable && (
        <Grid item md={12} xs={12}>
          <EntityArgoCDOverviewCard />
        </Grid>
      )}
      {!gitflicAvailable && !argocdAvailable && (
        <Grid item xs={12}>
          <CicdEmptyState />
        </Grid>
      )}
    </Grid>
  );
};

const catalogCicdEntityContent = EntityContentBlueprint.make({
  name: 'ci-cd',
  params: {
    path: '/ci-cd',
    title: 'CI/CD',
    filter: isCicdComponent,
    loader: async () => <CicdContent />,
  },
});

const catalogBindingsEntityContent = EntityContentBlueprint.make({
  name: 'bindings',
  params: {
    path: '/bindings',
    title: 'Права доступа',
    filter: isBindingsAvailable,
    loader: async () => <BindingsContent />,
  },
});

const catalogGitflicEntityContent = EntityContentBlueprint.make({
  name: 'gitflic',
  params: {
    path: '/gitflic',
    title: 'Gitflic',
    filter: entity => isComponent(entity) && isGitflicAvailable(entity),
    loader: async () => <GitflicIssuesMergeRequestsContent />,
  },
});

const deleteComponentContextMenuItem = EntityContextMenuItemBlueprint.make({
  name: 'delete-component',
  params: {
    icon: <DeleteOutlineIcon fontSize="small" />,
    filter: isComponent,
    useProps: () => {
      const { entity } = useEntity();
      const navigate = useNavigate();
      const { t } = useTranslationRef(appTranslationRef);
      const { allowed, loading } = useTemplateAvailability(
        'template:default/delete-component',
      );
      const deleteComponentTemplateUrl = buildTemplateUrl(
        'delete-component',
        entity,
      );

      return {
        title: (
          <Tooltip
            title={t('entityPage.contextMenu.deleteComponentTooltip')}
            placement="left"
            arrow
          >
            <span>{t('entityPage.contextMenu.deleteComponent')}</span>
          </Tooltip>
        ),
        disabled: loading || !allowed,
        onClick: () => navigate(deleteComponentTemplateUrl),
      };
    },
  },
});

const changeComponentOwnerContextMenuItem = EntityContextMenuItemBlueprint.make(
  {
    name: 'change-component-owner',
    params: {
      icon: <AssignmentIndIcon fontSize="small" />,
      filter: isComponent,
      useProps: () => {
        const { entity } = useEntity();
        const navigate = useNavigate();
        const { t } = useTranslationRef(appTranslationRef);
        const { allowed, loading } = useTemplateAvailability(
          'template:default/change-component-owner',
        );
        const changeOwnerTemplateUrl = buildTemplateUrl(
          'change-component-owner',
          entity,
        );

        return {
          title: (
            <Tooltip
              title={t('entityPage.contextMenu.changeComponentOwnerTooltip')}
              placement="left"
              arrow
            >
              <span>{t('entityPage.contextMenu.changeComponentOwner')}</span>
            </Tooltip>
          ),
          disabled: loading || !allowed,
          onClick: () => navigate(changeOwnerTemplateUrl),
        };
      },
    },
  },
);

export const catalogEntityPageModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    catalogCicdEntityContent,
    catalogBindingsEntityContent,
    catalogGitflicEntityContent,
    changeComponentOwnerContextMenuItem,
    deleteComponentContextMenuItem,
  ],
});
